import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { SiteModel } from 'src/app/models/site.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from 'src/app/shared/rbac.service';

@Component({
  selector: 'app-sites-main-sssp',
  templateUrl: './sites-main-sssp.component.html',
  styleUrls: ['./sites-main-sssp.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesMainSsspComponent implements OnInit {
  site: SiteModel = new SiteModel();

  // RBAC permission flags. These are resolved once in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess() on every change detection cycle.
  canCreateSafetyPlan: boolean = false;
  canEditSafetyPlan: boolean = false;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private route: ActivatedRoute,
    private router: Router,
    private rbac: RBACService
  ) {}

  // Expose RBACService to template
  protected readonly RBACService = RBACService;

  ngOnInit() {
    // Resolve the RBAC permission flags first so they are always set, even when we return early.
    this.canCreateSafetyPlan = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_SAFETY_PLANS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canEditSafetyPlan = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_SAFETY_PLANS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );

    // Check if user has permission to view sites and site safety plans
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view sites.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_SAFETY_PLANS,
          RBACService.RBAC_A.A_CREATE
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_SAFETY_PLANS,
          RBACService.RBAC_A.A_EDIT
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create or edit site safety plans.'
      );
      this.utils.redirectByLastSegment('details');
      return;
    }

    // Get the site id from the route params.
    const parent_id: number = Number(
      this.route.parent.snapshot.params['parent_id']
    );
    const child_id: number = Number(
      this.route.parent.snapshot.params['child_id']
    );

    // Check if we are updating a site or creating a new one.
    if (
      child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        parent_id)
    ) {
      // Store the site id.
      const site_id: number = child_id ? child_id : parent_id;
      // Make a request to get the site details.
      this.api.makeRequest('get', `v1/sites/${site_id}`).then((response) => {
        this.site.apply(response.data); // response.data is a legacy format that need to be used.
      });
    }
  }

  /**
   * Check if user can create site safety plans
   */
  canCreateSafetyPlans(): boolean {
    return this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_SAFETY_PLANS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
  }

  onCreateSSSP() {
    // Check permission before creating
    if (!this.canCreateSafetyPlans()) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create site safety plans.'
      );
      return;
    }

    this.api
      .makeRequest('post', 'v1/sssp', {
        site_id: this.site.id
      })
      .then((response: any) => {
        // This means the request was successful.
        this.site.sssp = response;

        // Check if user has edit permission to manage the safety plan
        const hasEditPermission = this.rbac.userCanAccess(
          [
            RBACService.getPermission(
              RBACService.RBAC_F.F_SITES_SAFETY_PLANS,
              RBACService.RBAC_A.A_EDIT
            )
          ],
          true
        );

        if (!hasEditPermission) {
          this.utils.showToast(
            'The Safety Plan was created successfully. You need edit permissions to manage it.'
          );
        }
      })
      .catch((error) => {
        const safety_plan_term = this.utils.getLangTerm(
          'safety-plans.singular',
          'Safety Plan'
        );
        this.utils.showModal(
          'Error',
          `Failed to create the Master ${safety_plan_term}.`
        );
      });
  }
}
